import { 
    Show, 
    SimpleShowLayout, 
    TextField, 
    DateField, 
    NumberField,
    FunctionField,
    TopToolbar,
    EditButton,
    ListButton
} from 'react-admin';
import { 
    Card, 
    CardContent, 
    Typography, 
    Grid, 
    Box, 
    Chip, 
    Avatar,
    Divider
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsIcon from '@mui/icons-material/Sports';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InfoIcon from '@mui/icons-material/Info';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UpdateIcon from '@mui/icons-material/Update';

const ShowActions = () => (
    <TopToolbar>
        <EditButton />
        <ListButton to="/leghe" />
    </TopToolbar>
);

const StatusChip = ({ record }) => {
    if (record.flAnnullato === 1) {
        return <Chip label="Annullata" color="error" icon={<CancelIcon />} />;
    }
    if (record.flAttivo === 1) {
        return <Chip label="Attiva" color="success" icon={<CheckCircleIcon />} />;
    }
    return <Chip label="Inattiva" color="default" />;
};

const VisibilityChip = ({ record }) => {
    return record.flVisibile === 1 ? 
        <Chip label="Visibile" color="info" icon={<VisibilityIcon />} /> :
        <Chip label="Nascosta" color="default" icon={<VisibilityOffIcon />} />;
};

export const Tbl_legaShow = props => (
    <Show {...props} actions={<ShowActions />} title="Dettagli Lega">
        <SimpleShowLayout>
            {/* Header Card */}
            <FunctionField render={record => (
                <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <CardContent>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                                    <EmojiEventsIcon sx={{ fontSize: 36, color: 'white' }} />
                                </Avatar>
                            </Grid>
                            <Grid item xs>
                                <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                                    {record.nome || 'Lega senza nome'}
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    ID: #{record.id}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                                    <StatusChip record={record} />
                                    <VisibilityChip record={record} />
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )} />

            {/* Informazioni Principali */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon color="primary" />
                        Informazioni Principali
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Nome Lega
                            </Typography>
                            <TextField source="nome" sx={{ fontWeight: 'bold' }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Sport
                            </Typography>
                            <FunctionField render={record => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SportsIcon color="action" />
                                    <Typography>Sport {record.sportId}</Typography>
                                </Box>
                            )} />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Note
                            </Typography>
                            <TextField source="note" multiline />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Ordinamento
                            </Typography>
                            <NumberField source="ordinamento" />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Stato e Configurazione */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon color="primary" />
                        Stato e Configurazione
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Stato Lega
                            </Typography>
                            <FunctionField render={record => <StatusChip record={record} />} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Visibilità
                            </Typography>
                            <FunctionField render={record => <VisibilityChip record={record} />} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Ordinamento
                            </Typography>
                            <Chip label={`Posizione: ${props.record?.ordinamento || 0}`} variant="outlined" />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Informazioni Temporali */}
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon color="primary" />
                        Informazioni Temporali
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Data Creazione
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon fontSize="small" color="action" />
                                <DateField source="createdAt" showTime locales="it-IT" />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Ultimo Aggiornamento
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <UpdateIcon fontSize="small" color="action" />
                                <DateField source="updatedAt" showTime locales="it-IT" />
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </SimpleShowLayout>
    </Show>
);
            